/**
 * Scanner Utility for IMEI and Barcode Detection
 * Used in Mobile Industry POS for product search
 */

export type ScanType = 'imei' | 'barcode' | 'unknown';

export interface ScanResult {
    type: ScanType;
    value: string;
    isValid: boolean;
}

/**
 * Scanner Utility Class
 * Handles IMEI detection using Luhn algorithm validation
 */
export class ScannerUtil {
    /**
     * Evaluates input and classifies it as IMEI or Barcode
     * 
     * @param input - The scanned or typed input string
     * @returns ScanResult with type, value, and validation status
     * 
     * @example
     * ScannerUtil.detectInputType('356789123456789') // { type: 'imei', value: '356789123456789', isValid: true }
     * ScannerUtil.detectInputType('8901234567890')   // { type: 'barcode', value: '8901234567890', isValid: true }
     */
    static detectInputType(input: string): ScanResult {
        const rawValue = input?.trim() || '';

        if (!rawValue) {
            return { type: 'unknown', value: rawValue, isValid: false };
        }

        // 1. If it contains non-numeric characters, it's definitively a barcode
        if (!/^\d+$/.test(rawValue)) {
            return { type: 'barcode', value: rawValue, isValid: true };
        }

        // 2. If it's a 15-digit number, validate using Luhn Algorithm
        if (rawValue.length === 15) {
            if (this.isLuhnValid(rawValue)) {
                return { type: 'imei', value: rawValue, isValid: true };
            } else {
                // 15 digits but failed Luhn? It's a barcode.
                return { type: 'barcode', value: rawValue, isValid: true };
            }
        }

        // 3. Common barcode lengths (UPC-A: 12, EAN-13: 13, EAN-8: 8, Code 39: variable)
        if ([8, 12, 13].includes(rawValue.length)) {
            return { type: 'barcode', value: rawValue, isValid: true };
        }

        // 4. Any other length numeric string is treated as barcode
        return { type: 'barcode', value: rawValue, isValid: true };
    }

    /**
     * Luhn Algorithm (Mod 10) Check
     * Standard validation for IMEI, credit cards, etc.
     * 
     * @param imei - The 15-digit IMEI string to validate
     * @returns true if IMEI passes Luhn checksum validation
     * 
     * @example
     * ScannerUtil.isLuhnValid('356789123456789') // true or false
     */
    static isLuhnValid(imei: string): boolean {
        if (!imei || imei.length !== 15 || !/^\d+$/.test(imei)) {
            return false;
        }

        let sum = 0;
        let isSecond = false;

        // Loop through digits from right to left
        for (let i = imei.length - 1; i >= 0; i--) {
            let d = parseInt(imei[i], 10);

            if (isSecond) {
                d *= 2;
                if (d > 9) {
                    d -= 9;
                }
            }

            sum += d;
            isSecond = !isSecond;
        }

        return sum % 10 === 0;
    }

    /**
     * Validates if input is a valid IMEI
     * Convenience method for direct IMEI validation
     * 
     * @param input - The input string to validate as IMEI
     * @returns true if input is a valid IMEI
     */
    static isValidIMEI(input: string): boolean {
        const result = this.detectInputType(input);
        return result.type === 'imei' && result.isValid;
    }

    /**
     * Formats IMEI for display (XX-XXXXXX-XXXXXX-X)
     * 
     * @param imei - The 15-digit IMEI string
     * @returns Formatted IMEI string
     */
    static formatIMEI(imei: string): string {
        if (!imei || imei.length !== 15) {
            return imei;
        }
        return `${imei.substring(0, 2)}-${imei.substring(2, 8)}-${imei.substring(8, 14)}-${imei.substring(14)}`;
    }
}
